/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useRef } from 'react';

import type { AriaListBoxOptions } from '@react-aria/listbox';
import type { Node } from '@react-types/shared';
import { useListBox, useOption } from 'react-aria';
import type { ListState } from 'react-stately';
import { styled } from 'styled-components';

import { ReactComponent as IconCheck } from './icon-check.svg';

interface ListBoxProps<T> extends AriaListBoxOptions<T> {
  state: ListState<T>;
  isWidget?: boolean;
  listBoxRef?: React.RefObject<HTMLUListElement>;
}

interface OptionProps<T> {
  item: Node<T>;
  state: ListState<T>;
}

const Icon = styled(IconCheck)``;

const List = styled.ul`
  min-width: 220px;
  background-color: var(--backgroundPrimary);
  box-shadow: var(--sElevation_3);
  border-radius: 12px;
  overflow: hidden;

  &.widget {
    max-height: 150px;
    overflow-y: scroll;
  }
`;

const ListItem = styled.li`
  display: flex;
  align-items: center;
  column-gap: 8px;
  padding: 12px 48px 12px 16px;
  height: 48px;
  cursor: pointer;
  position: relative;

  &[data-current='true'] {
    background-color: var(--backgroundSecondary);
  }

  & ${Icon} {
    position: absolute;
    right: 16px;
    color: var(--contentTertiary);
  }
`;

const Option = <T,>({ item, state }: OptionProps<T>) => {
  const ref = useRef<HTMLLIElement>(null);

  const { optionProps, isFocused, isSelected } = useOption(
    {
      key: item.key,
    },
    state,
    ref,
  );

  return (
    <ListItem {...optionProps} ref={ref} data-current={isFocused}>
      {item.rendered}
      {isSelected && <Icon />}
    </ListItem>
  );
};

//@ts-ignore
//@ts-nocheck
const ListBox = <T,>(props: ListBoxProps<T>) => {
  const ref = useRef<HTMLUListElement>(null);
  const { listBoxRef = ref, state } = props;

  // @ts-ignore
  const { listBoxProps } = useListBox(props, state, listBoxRef);

  return (
    <List {...listBoxProps} ref={listBoxRef} className={props?.isWidget ? 'widget' : undefined}>
      {[...state.collection].map((item) => (
        <Option key={item.key} item={item} state={state} />
      ))}
    </List>
  );
};

export default ListBox;
